import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SaveStateManager } from '../features/save-state'
import type { SceneData, SceneConfig, ChoiceCondition } from '../features/scenes/types'
import { renderChapterTitle } from '../features/scenes/renderers/chapter-title-renderer'
import { renderMinigame } from '../features/scenes/renderers/minigame-renderer'
import { renderHomework } from '../features/scenes/renderers/homework-renderer'
import { applySpacing, setupSpacingResizeListener } from '../features/scenes/utils/spacing-manager'
import { appendSceneNumberDebug } from '../features/scenes/utils/debug-helper'
import { setupSceneAfterImageLoad } from '../features/scenes/utils/scene-setup'
import { showScene } from '../features/scenes/utils/scene-visibility'
import { cleanupScene } from '../features/scenes/utils/scene-cleanup'
import { renderStickyLayout, setupStickyBehavior } from '../features/scenes/renderers/sticky-layout-renderer'
import { renderScrollPan } from '../features/scenes/renderers/scroll-pan-renderer'
import { renderHorizontalScroll } from '../features/scenes/renderers/horizontal-scroll-renderer'

import { setupDefaultSceneImage } from '../features/scenes/renderers/default-scene-setup'
import { getLastUnlockedBubble, getLastClickedActionPoint } from '../features/scenes/speech-bubbles'
import { filterBubblesByCondition } from '../features/scenes/speech-bubbles/bubble-filter'
import { createActionPoint } from '../features/scenes/speech-bubbles/action-points'
import { setupActionPoints as setupActionPointsManager } from '../features/scenes/speech-bubbles/action-points-setup'
import {
  setupScrollLock as setupScrollLockManager,
  type ScrollLockHandle,
} from '../features/scenes/speech-bubbles/scroll-lock'
import { handleActionPointClick } from '../features/scenes/speech-bubbles/action-point-handler'
import { restoreBubbleState } from '../features/scenes/speech-bubbles/state-restoration'
import { setupHomeworkOverlay } from '../features/scenes/homework'
import { evaluateCondition } from '../features/scenes/utils/condition-evaluator'
import { resolveSceneBg, getSceneImagePath, applyResponsiveImage } from '../utils/pathResolver'
import track, { SELECT_CHOICE } from '../features/analytics'
import {
  showChoices as showChoicesManager,
  handleChoiceClick,
  handleChoiceSelection,
  createConfirmationButton,
  removeConfirmationButton,
  restoreChoiceState as restoreChoiceStateManager,
  resolveChoiceResult,
} from '../features/scenes/choices'
import { getAudioManager } from '../features/audio'
import { showGlobalScrollIndicator, hideGlobalScrollIndicator } from '../utils/scroll-indicator'
import {
  setupScrollIntoCenterSound,
  setupStaticImages,
  runSceneAnimationWhenReady,
  setupElementAnimations,
  type SceneAnimationInstance,
} from '../features/scenes/animations'

gsap.registerPlugin(ScrollTrigger)

// Note: ScrollTrigger markers are disabled via CSS in main.scss (utilities)

// Re-export types for backward compatibility
export type { SceneData, ChoiceCondition, SceneConfig } from '../features/scenes/types'

/**
 * Renders a single story scene. Top-level flow: constructor → render() (by type) → show().
 * Choices, bubbles, restore, and animations are delegated to features (scenes/choices, speech-bubbles, animations).
 */
export class StoryScene {
  private data: SceneData
  private container: HTMLDivElement
  private wrapper: HTMLDivElement | null = null
  private imageContainer: HTMLDivElement | null = null
  private onSceneComplete?: (nextSceneId?: number) => void
  private scrollLockedAtEnd = false
  private deferredStickyActivation = false
  private actionPoints: HTMLDivElement[] = []
  private currentActionPointIndex = 0
  private onUnlockCallback?: () => void
  private fadeInAnimation: gsap.core.Tween | null = null
  private onBubbleShownCallback?: (bubbleId: string) => void
  private onActionPointClickedCallback?: (actionPointIndex: number) => void
  private unlockedBubbleIds: Set<string> = new Set()
  private clickedActionPointIndices: Set<number> = new Set()
  private config: SceneConfig
  private choiceButtons: HTMLButtonElement[] = []
  private choicesShown = false
  private choiceSelected = false
  private selectedChoice: number | null = null
  private confirmationButton: HTMLButtonElement | null = null
  private scrollIndicatorCleanup: (() => void) | null = null
  private onLoadSceneCallback?: (sceneId: string) => void // Callback to load a scene by ID
  private onEvaluateCondition?: (condition: ChoiceCondition) => boolean // Callback to evaluate conditions
  private sceneAnimationInstance: SceneAnimationInstance | null = null
  private spacingResizeHandler: (() => void) | null = null
  private chapterTitleContainer: HTMLDivElement | null = null
  private chapterTitleAudioPlayed = false
  private lastScrollY = 0
  private scrollSoundCleanups: Array<() => void> = []
  private homeworkButton: HTMLElement | null = null
  private homeworkOverlay: HTMLElement | null = null
  private homeworkEscapeHandler: ((e: KeyboardEvent) => void) | null = null
  private onSceneDebugClickCallback?: (sceneIndex: number) => void
  private onAfterShowCallback: (() => void) | null = null
  private scrollLockHandle: ScrollLockHandle | null = null

  constructor(
    data: SceneData,
    private parentContainer: HTMLElement,
    config: SceneConfig,
    private sceneIndex?: number,
  ) {
    this.data = { ...data }
    this.config = config
    this.wrapper = this.createWrapper()
    this.container = this.createContainer()
    this.wrapper.appendChild(this.container)
  }

  private createWrapper(): HTMLDivElement {
    const wrapper = document.createElement('div')
    wrapper.className = 'scene-wrapper'
    wrapper.setAttribute('data-scene', this.data.id)
    if (this.data.spacer === false) wrapper.setAttribute('data-no-spacer', '')
    if (this.data.overflow) wrapper.style.overflow = this.data.overflow
    return wrapper
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div')
    container.className = this.getContainerClassName()
    if (this.data.align) {
      const alignMap: Record<string, string> = { start: 'flex-start', end: 'flex-end', center: 'center' }
      container.style.alignItems = alignMap[this.data.align] ?? this.data.align
    }
    return container
  }

  private getContainerClassName(): string {
    switch (this.data.type) {
      case 'chapter-title':
        return 'chapter-title-scene'
      case 'minigame':
        return 'scene minigame-scene'
      case 'homework':
        return 'scene homework-scene'
      default:
        return 'scene'
    }
  }

  getContainer(): HTMLDivElement {
    return this.container
  }

  getWrapper(): HTMLDivElement | null {
    return this.wrapper
  }

  private applySpacing(): void {
    applySpacing({
      wrapper: this.wrapper,
      imageContainer: this.imageContainer,
      data: this.data,
    })
  }

  private setupSpacingResizeListener(): void {
    const cleanup = setupSpacingResizeListener(
      {
        wrapper: this.wrapper,
        imageContainer: this.imageContainer,
        data: this.data,
      },
      () => this.applySpacing(),
    )
    this.spacingResizeHandler = cleanup
  }

  // --- Render by type ---

  /** Dispatch by scene type; default scene uses image + bubbles/animations. */
  render(): HTMLElement {
    if (Array.isArray(this.data.bg)) {
      this.data.bg = resolveSceneBg(this.data, (c) => this.evaluateCondition(c)) ?? false
    }

    if (this.data.type === 'chapter-title') {
      return this.renderChapterTitle()
    }
    if (this.data.type === 'minigame') {
      return this.renderMinigame()
    }
    if (this.data.type === 'homework') {
      return this.renderHomework()
    }
    if (this.data.layout?.type === 'sticky') {
      return renderStickyLayout({
        container: this.container,
        wrapper: this.wrapper,
        imageContainer: this.imageContainer,
        data: this.data,
        onImageContainerSet: (container) => {
          this.imageContainer = container
        },
        onAppendSceneNumberDebug: () => {
          this.appendSceneNumberDebug()
        },
        onAfterImageLoad: () => this.setupStickySceneAfterImageLoad(),
      })
    }
    if (this.data.layout?.type === 'horizontal-scroll') {
      return renderHorizontalScroll({
        container: this.container,
        wrapper: this.wrapper,
        imageContainer: this.imageContainer,
        data: this.data,
        onImageContainerSet: (container) => {
          this.imageContainer = container
        },
        onAppendSceneNumberDebug: () => {
          this.appendSceneNumberDebug()
        },
        onAfterImageLoad: () => this.setupStickySceneAfterImageLoad(),
      })
    }
    if (this.data.layout?.type === 'scroll-pan') {
      return renderScrollPan({
        container: this.container,
        wrapper: this.wrapper,
        imageContainer: this.imageContainer,
        data: this.data,
        onImageContainerSet: (container) => {
          this.imageContainer = container
        },
        onAppendSceneNumberDebug: () => {
          this.appendSceneNumberDebug()
        },
      })
    }

    this.imageContainer = this.renderDefaultScene()
    this.appendSceneNumberDebug()

    return this.container
  }

  /** Render default (image-based) scene: set up image, then on load run bubbles/animations. */
  private renderDefaultScene(): HTMLDivElement | null {
    const { imageContainer } = setupDefaultSceneImage({
      data: this.data,
      container: this.container,
      onImageLoad: () => this.setupDefaultSceneAfterImageLoad(),
      onImageError: () => this.showPlaceholderOnImageError(),
    })
    return imageContainer
  }

  /** Post-load setup for sticky/horizontal-scroll layouts: everything except scroll lock. */
  private setupStickySceneAfterImageLoad(): void {
    if (this.data.images && this.data.images.length > 0) {
      this.setupStaticImages()
    }
    if (this.data.speechBubbles && this.data.speechBubbles.length > 0) {
      this.setupActionPoints()
    }
    if (this.data.padding || this.data.margin) {
      this.applySpacing()
      this.setupSpacingResizeListener()
    }
    if (this.data.animations && this.data.animations.length > 0) {
      this.setupAnimations()
    }
    if (this.data.animation) {
      this.setupSceneAnimation()
    }
    if (this.data.audio?.sounds) {
      for (const sound of this.data.audio.sounds) {
        this.setupScrollIntoCenterSound(this.container, this.data.id, sound.file, {
          optional: true,
          trigger: sound.trigger,
        })
      }
    }
  }

  private setupDefaultSceneAfterImageLoad(): void {
    setupSceneAfterImageLoad({
      data: this.data,
      config: this.config,
      container: this.container,
      imageContainer: this.imageContainer,
      clickedActionPointIndices: this.clickedActionPointIndices,
      onApplySpacing: () => this.applySpacing(),
      onSetupSpacingResizeListener: () => this.setupSpacingResizeListener(),
      onSetupActionPoints: () => this.setupActionPoints(),
      onSetupScrollLock: () => this.setupScrollLock(),
      onSetupAnimations: () => this.setupAnimations(),
      onSetupSceneAnimation: () => this.setupSceneAnimation(),
      onSetupScrollIntoCenterSound: (el, sceneId, fileName, opts) =>
        this.setupScrollIntoCenterSound(el, sceneId, fileName, opts),
    })
  }

  private showPlaceholderOnImageError(): void {
    const placeholder = document.createElement('div')
    placeholder.className = 'scene__placeholder'
    placeholder.innerHTML = `
      <div>${this.data.id}</div>
      <div class="scene__placeholder-text">Scene ${this.data.id}</div>
    `
    this.imageContainer?.appendChild(placeholder)
  }

  setOnSceneDebugClick(callback: (sceneIndex: number) => void): void {
    this.onSceneDebugClickCallback = callback
  }

  private renderMinigame(): HTMLElement {
    renderMinigame({
      container: this.container,
      data: this.data,
      onComplete: () => {
        if (this.onSceneComplete) {
          this.onSceneComplete()
        }
      },
    })
    this.appendSceneNumberDebug()
    return this.container
  }

  private renderHomework(): HTMLElement {
    renderHomework({
      container: this.container,
      data: this.data,
      config: this.config,
      onOverlaySetup: () => {
        this.setupHomeworkOverlay()
      },
    })
    this.appendSceneNumberDebug()
    return this.container
  }

  private appendSceneNumberDebug(): void {
    appendSceneNumberDebug({
      container: this.container,
      data: this.data,
      sceneIndex: this.sceneIndex,
      onSceneDebugClick: this.onSceneDebugClickCallback,
    })
  }

  private setupHomeworkOverlay(): void {
    // Support both old format (text) and new format (content)
    if (!this.data.content && !this.data.text) return

    // Convert old format to new format if needed
    const content = this.data.content || {
      description: this.data.text || '',
    }

    const refs = setupHomeworkOverlay({
      container: this.container,
      sceneId: this.data.id,
      content: content,
    })

    this.homeworkButton = refs.button
    this.homeworkOverlay = refs.overlay
    this.homeworkEscapeHandler = refs.escapeHandler
  }

  private renderChapterTitle(): HTMLElement {
    const chapterContainer = renderChapterTitle({
      container: this.container,
      data: this.data,
      config: this.config,
      onAudioSetup: (container: HTMLDivElement) => {
        this.chapterTitleContainer = container
        this.setupChapterTitleAudio()
      },
    })
    this.chapterTitleContainer = chapterContainer

    this.appendSceneNumberDebug()

    return this.container
  }

  private setupChapterTitleAudio(): void {
    if (this.data.type !== 'chapter-title' || !this.chapterTitleContainer) return
    this.setupScrollIntoCenterSound(
      this.chapterTitleContainer,
      this.data.id,
      getAudioManager().getChapterTitleSound(),
      { volumeCategory: 'speech' },
    )
  }

  private setupScrollIntoCenterSound(
    element: HTMLElement,
    sceneId: string,
    fileName: string,
    options?: {
      optional?: boolean
      trigger?: { start: { element: number; viewport: number } }
      volumeCategory?: 'speech' | 'sound'
    },
  ): void {
    const handler = setupScrollIntoCenterSound(element, sceneId, fileName, options)
    this.scrollSoundCleanups.push(handler.cleanup)
  }

  private setupScrollLock(): void {
    const filteredBubbles = filterBubblesByCondition(this.data.speechBubbles, this.onEvaluateCondition)
    this.scrollLockHandle = setupScrollLockManager({
      imageContainer: this.imageContainer,
      speechBubbles: filteredBubbles,
      clickedActionPointIndices: this.clickedActionPointIndices,
      scrollLockMargin: this.data.scrollLockMargin,
    })
    this.scrollLockedAtEnd = true
  }

  private activateDeferredSticky(): void {
    if (!this.wrapper || !this.imageContainer) return
    setupStickyBehavior({
      wrapper: this.wrapper,
      imageContainer: this.imageContainer,
      data: this.data,
    })
  }

  show(animate: boolean = true): void {
    this.appendWrapperToParent()
    if (this.onAfterShowCallback) {
      const cb = this.onAfterShowCallback
      this.onAfterShowCallback = null
      cb()
    }
    this.scheduleApplySpacingIfNeeded()
    showScene(
      {
        container: this.container,
        fadeInAnimation: this.fadeInAnimation,
        setFadeInAnimation: (a) => {
          this.fadeInAnimation = a
        },
      },
      animate,
    )
  }

  private appendWrapperToParent(): void {
    if (this.wrapper && this.wrapper.parentNode !== this.parentContainer) {
      this.parentContainer.appendChild(this.wrapper)
    }
  }

  private scheduleApplySpacingIfNeeded(): void {
    if (!this.data.padding && !this.data.margin) return
    requestAnimationFrame(() => requestAnimationFrame(() => this.applySpacing()))
  }

  private setupActionPoints(): void {
    setupActionPointsManager({
      imageContainer: this.imageContainer,
      data: this.data,
      clickedActionPointIndices: this.clickedActionPointIndices,
      onCreateActionPoint: (xPercent, yPercent, bubbleIndex, actionPointIndex) => {
        this.createActionPoint(xPercent, yPercent, bubbleIndex, actionPointIndex)
        this.currentActionPointIndex = bubbleIndex + 1
      },
      onEvaluateCondition: this.onEvaluateCondition,
    })
  }

  private setupSceneAnimation(): void {
    if (!this.data.animation) return
    if (this.sceneAnimationInstance) {
      this.sceneAnimationInstance.__cleanup?.()
      this.sceneAnimationInstance.kill()
      this.sceneAnimationInstance = null
    }
    runSceneAnimationWhenReady(this.container, this.data, (instance) => {
      this.sceneAnimationInstance = instance
    })
  }

  private setupStaticImages(): void {
    setupStaticImages({
      data: this.data,
      config: this.config,
      container: this.container,
      imageContainer: this.imageContainer,
    })
  }

  private setupAnimations(): void {
    if (!this.imageContainer || !this.data.animations) return
    setupElementAnimations({
      imageContainer: this.imageContainer,
      data: this.data,
      config: this.config,
    })
  }

  setOnComplete(callback: (nextSceneId?: number) => void): void {
    this.onSceneComplete = callback
  }

  setOnUnlock(callback: () => void): void {
    this.onUnlockCallback = callback
  }

  setOnAfterShow(callback: () => void): void {
    this.onAfterShowCallback = callback
  }

  setOnBubbleShown(callback: (bubbleId: string) => void): void {
    this.onBubbleShownCallback = callback
  }

  setOnActionPointClicked(callback: (actionPointIndex: number) => void): void {
    this.onActionPointClickedCallback = callback
  }

  setOnLoadScene(callback: (sceneId: string) => void): void {
    this.onLoadSceneCallback = callback
  }

  setOnEvaluateCondition(callback: (condition: ChoiceCondition) => boolean): void {
    this.onEvaluateCondition = callback
  }

  /**
   * Programmatically select a choice (for debug/testing purposes)
   */
  selectChoice(choiceNumber: 1 | 2): void {
    if (!this.data.choices) return

    // Ensure choices are shown first
    if (!this.choicesShown) {
      this.showChoices()
      // Wait for choices to be rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.restoreChoiceState(choiceNumber)
        })
      })
    } else {
      // Choices already shown, just restore the choice state
      this.restoreChoiceState(choiceNumber)
    }
  }

  /**
   * Evaluate a choice condition
   */
  private evaluateCondition(condition: ChoiceCondition): boolean {
    // Use callback if provided, otherwise use default evaluator
    if (this.onEvaluateCondition) {
      return this.onEvaluateCondition(condition)
    }
    return evaluateCondition(condition)
  }

  /**
   * Resolve the scene ID from a choice result, evaluating conditions if needed
   */
  private resolveChoiceResult(
    result: string | { sceneId: string; when?: ChoiceCondition } | Array<{ sceneId: string; when: ChoiceCondition }>,
  ): string | null {
    return resolveChoiceResult(result, this.onEvaluateCondition)
  }

  // --- Restore (bubbles + choices) ---

  getLastUnlockedBubble(): number | null {
    return getLastUnlockedBubble(this.unlockedBubbleIds)
  }

  getLastClickedActionPoint(): number | null {
    return getLastClickedActionPoint(this.clickedActionPointIndices)
  }

  restoreState(lastBubbleId: string | null, lastActionPointIndex: number | null): void {
    const actualLastIndex = restoreBubbleState(lastBubbleId, lastActionPointIndex, this.getRestoreBubbleContext())
    this.scheduleShowChoicesIfRestored(actualLastIndex)
    this.updateScrollLockAfterRestore(actualLastIndex)
  }

  private getRestoreBubbleContext() {
    return {
      data: this.data,
      config: this.config,
      imageContainer: this.imageContainer,
      clickedActionPointIndices: this.clickedActionPointIndices,
      actionPoints: this.actionPoints,
      currentActionPointIndex: this.currentActionPointIndex,
      unlockedBubbleIds: this.unlockedBubbleIds,
      onCreateActionPoint: (x: number, y: number, bi: number, ai: number) => {
        this.createActionPoint(x, y, bi, ai)
        this.currentActionPointIndex = bi + 1
      },
      onShowChoices: () => this.showChoices(),
      onEvaluateCondition: this.onEvaluateCondition,
    }
  }

  private scheduleShowChoicesIfRestored(actualLastIndex: number): void {
    if (!this.data.choices || actualLastIndex < 0) return
    requestAnimationFrame(() => requestAnimationFrame(() => this.showChoices()))
  }

  private updateScrollLockAfterRestore(actualLastIndex: number): void {
    const bubbleCount = (this.data.speechBubbles || []).length
    if (actualLastIndex >= 0 && actualLastIndex >= bubbleCount - 1) {
      this.scrollLockedAtEnd = false
      if (this.deferredStickyActivation) {
        this.deferredStickyActivation = false
        this.activateDeferredSticky()
      }
    }
    this.checkHorizontalScrollUnlock()
    this.checkBgSwap(true)
  }

  private createActionPoint(xPercent: number, yPercent: number, bubbleIndex: number, _actionPointIndex: number): void {
    if (!this.imageContainer) return

    const actionPoint = createActionPoint(this.imageContainer, xPercent, yPercent, bubbleIndex, {
      onClick: (actionPoint, bubbleIndex, xPercent, yPercent, wasKeyboard) => {
        this.onActionPointClick(actionPoint, bubbleIndex, xPercent, yPercent, wasKeyboard)
      },
    })

    this.actionPoints.push(actionPoint)
  }

  private getActionPointClickContext() {
    return {
      data: this.data,
      config: this.config,
      imageContainer: this.imageContainer,
      clickedActionPointIndices: this.clickedActionPointIndices,
      actionPoints: this.actionPoints,
      currentActionPointIndex: this.currentActionPointIndex,
      unlockedBubbleIds: this.unlockedBubbleIds,
      onBubbleShown: this.onBubbleShownCallback,
      onActionPointClicked: (idx: number) => {
        this.scrollLockHandle?.recalculate()
        this.onActionPointClickedCallback?.(idx)
      },
      onUnlock: () => {
        this.scrollLockedAtEnd = false
        if (this.deferredStickyActivation) {
          this.deferredStickyActivation = false
          this.activateDeferredSticky()
        }
        this.onUnlockCallback?.()
      },
      onShowChoices: () => {
        if (!this.choicesShown) this.showChoices()
      },
      onCreateActionPoint: (x: number, y: number, bi: number, ai: number) => {
        this.createActionPoint(x, y, bi, ai)
        this.currentActionPointIndex = bi + 1
      },
      onEvaluateCondition: this.onEvaluateCondition,
    }
  }

  private onActionPointClick(
    actionPoint: HTMLDivElement,
    bubbleIndex: number,
    _xPercent: number,
    _yPercent: number,
    wasKeyboard?: boolean,
  ): void {
    handleActionPointClick(actionPoint, bubbleIndex, this.getActionPointClickContext(), wasKeyboard)
    this.checkHorizontalScrollUnlock()
    this.checkBgSwap()
  }

  private checkHorizontalScrollUnlock(): void {
    const unlockAfter = this.data.layout?.config?.unlockAfter
    if (!unlockAfter || !this.wrapper) return
    if (this.clickedActionPointIndices.size >= unlockAfter) {
      ;(this.wrapper as any).__horizontalScrollUnlock?.()
      ;(this.wrapper as any).__stickyScrollUnlock?.()
    }
  }

  private bgSwapped = false

  private checkBgSwap(silent = false): void {
    const swap = this.data.bgSwap
    if (!swap || !this.imageContainer || this.bgSwapped) return
    if (this.clickedActionPointIndices.size >= swap.afterActionPoint) {
      const img = this.imageContainer.querySelector('img.scene__img') as HTMLImageElement | null
      if (!img) return
      img.removeAttribute('srcset')
      img.removeAttribute('sizes')
      img.src = getSceneImagePath(this.data.id, swap.src)
      applyResponsiveImage(img)
      this.bgSwapped = true
      if (!silent) getAudioManager().tryPlayCustomSound('papir.mp3', this.data.id)
    }
  }

  // --- Choices ---

  /** Context object passed to choice manager / handleChoiceClick / restoreChoiceState. */
  private getChoiceContext() {
    return {
      data: this.data,
      config: this.config,
      container: this.container,
      onLoadScene: this.onLoadSceneCallback,
      onEvaluateCondition: this.onEvaluateCondition,
    }
  }

  private showChoices(): void {
    if (!this.data.choices || this.choicesShown) return

    const savedChoice = SaveStateManager.load()?.choices?.[this.data.id]
    if (savedChoice) {
      this.selectedChoice = savedChoice
      this.choiceSelected = true
    }

    this.choicesShown = true

    // Add margin-bottom to scene container for choice scenes (only if not already confirmed)
    if (this.container.classList.contains('scene') && !this.choiceSelected) {
      this.container.style.marginBottom = '10rem'
    }

    showChoicesManager(this.getChoiceContext(), (choiceNumber) => this.onChoiceClick(choiceNumber), this.choiceButtons)

    if (savedChoice) {
      requestAnimationFrame(() => this.restoreChoiceState(savedChoice))
    }
  }

  private onChoiceClick(choiceNumber: number): void {
    if (!this.data.choices || this.choiceSelected) return

    this.selectedChoice = choiceNumber

    handleChoiceSelection(choiceNumber, this.getChoiceContext(), this.choiceButtons)
    if (this.data.choices.sound) {
      getAudioManager().tryPlayCustomSound(this.data.choices.sound, this.data.id)
    }

    // Show confirmation button if not already shown
    if (!this.confirmationButton) {
      this.confirmationButton = createConfirmationButton(
        this.getChoiceContext(),
        () => {
          this.onConfirmChoice()
        },
        () => {
          // After animation completes, check if button is in viewport
          if (this.confirmationButton && !this.isElementInViewport(this.confirmationButton)) {
            this.setupScrollIndicatorForButton(this.confirmationButton)
          }
        },
      )
    }
  }

  private onConfirmChoice(): void {
    if (this.choiceSelected || !this.data.choices || this.selectedChoice === null) return

    this.choiceSelected = true
    const choiceNumber = this.selectedChoice

    // Clean up scroll indicator if it was showing
    if (this.scrollIndicatorCleanup) {
      this.scrollIndicatorCleanup()
      this.scrollIndicatorCleanup = null
    }

    // Remove margin-bottom from scene container
    if (this.container.classList.contains('scene')) {
      this.container.style.marginBottom = ''
    }

    // Remove confirmation button
    if (this.confirmationButton) {
      removeConfirmationButton(this.getChoiceContext())
      this.confirmationButton = null
    }

    const sceneId = handleChoiceClick(choiceNumber, this.getChoiceContext(), this.choiceButtons)
    if (!sceneId) {
      console.error(`No valid scene result found for choice ${choiceNumber}`)
      return
    }

    track(SELECT_CHOICE, this.data.id, String(choiceNumber))
    if (this.onLoadSceneCallback) {
      this.onLoadSceneCallback(sceneId)
    } else {
      console.error('No scene loader callback set for choice scene result')
    }
  }

  private isElementInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    // Check if element is mostly visible (at least 50% of its height is in viewport)
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    return visibleHeight >= rect.height * 0.5 && rect.top >= 0
  }

  private setupScrollIndicatorForButton(button: HTMLElement): void {
    // Clean up any existing listener
    if (this.scrollIndicatorCleanup) {
      this.scrollIndicatorCleanup()
      this.scrollIndicatorCleanup = null
    }

    // Show scroll indicator
    showGlobalScrollIndicator()

    // Function to check if button is in viewport
    const checkButtonVisibility = () => {
      // Check if button still exists (might have been removed)
      if (!this.confirmationButton || !button.parentNode) {
        hideGlobalScrollIndicator()
        if (this.scrollIndicatorCleanup) {
          this.scrollIndicatorCleanup()
          this.scrollIndicatorCleanup = null
        }
        return
      }

      // Check if button is now in viewport
      if (this.isElementInViewport(button)) {
        hideGlobalScrollIndicator()
        if (this.scrollIndicatorCleanup) {
          this.scrollIndicatorCleanup()
          this.scrollIndicatorCleanup = null
        }
      }
    }

    // Set up scroll and resize listeners
    const handleScroll = () => {
      checkButtonVisibility()
    }

    const handleResize = () => {
      checkButtonVisibility()
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    // Store cleanup function
    this.scrollIndicatorCleanup = () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      hideGlobalScrollIndicator()
    }

    // Initial check
    checkButtonVisibility()
  }

  private restoreChoiceState(choiceNumber: number): void {
    const sceneId = restoreChoiceStateManager(choiceNumber, this.getChoiceContext())
    if (sceneId && this.onLoadSceneCallback) {
      this.onLoadSceneCallback(sceneId)
    }
  }

  destroy(): void {
    this.clearSceneAnimationInstance()

    if (this.wrapper && (this.wrapper as any).__stickyLayoutCleanup) {
      ;(this.wrapper as any).__stickyLayoutCleanup()
    }
    if (this.wrapper && (this.wrapper as any).__scrollPanCleanup) {
      ;(this.wrapper as any).__scrollPanCleanup()
    }
    if (this.wrapper && (this.wrapper as any).__horizontalScrollCleanup) {
      ;(this.wrapper as any).__horizontalScrollCleanup()
    }

    // Clean up minigame resources (e.g., scroll indicator)
    if (this.container) {
      const minigameRoot = this.container.querySelector('[data-minigame]') as HTMLElement
      if (minigameRoot && (minigameRoot as any).__minigameCleanup) {
        ;(minigameRoot as any).__minigameCleanup()
      }
    }

    cleanupScene({
      fadeInAnimation: this.fadeInAnimation,
      spacingResizeHandler: this.spacingResizeHandler,
      scrollSoundCleanups: this.scrollSoundCleanups,
      homeworkEscapeHandler: this.homeworkEscapeHandler,
      homeworkButton: this.homeworkButton,
      homeworkOverlay: this.homeworkOverlay,
      rainSceneTriggers: [],
    })
    this.clearAllSceneReferences()
    this.removeWrapperFromDOM()
  }

  private clearSceneAnimationInstance(): void {
    if (!this.sceneAnimationInstance) return
    this.sceneAnimationInstance.__cleanup?.()
    this.sceneAnimationInstance.kill()
    this.sceneAnimationInstance = null
  }

  private clearAllSceneReferences(): void {
    this.spacingResizeHandler = null
    this.scrollSoundCleanups = []
    this.homeworkEscapeHandler = null
    this.homeworkButton = null
    this.homeworkOverlay = null
    this.fadeInAnimation = null
    this.rainSceneTriggers = []
    this.bgSwapped = false
    this.scrollLockHandle?.destroy()
    this.scrollLockHandle = null
    if (this.scrollIndicatorCleanup) {
      this.scrollIndicatorCleanup()
      this.scrollIndicatorCleanup = null
    }
  }

  private removeWrapperFromDOM(): void {
    if (this.wrapper?.parentNode) {
      this.wrapper.parentNode.removeChild(this.wrapper)
    }
  }
}
